import {
  DrupalFile,
  DrupalMedia,
  DrupalMenuLinkContent,
  DrupalNode,
  DrupalParagraph,
  DrupalTaxonomyTerm, PathAlias
} from "next-drupal";
import {DrupalSearchApiFacet, JsonApiResourceWithPath} from "next-drupal/src/types";

// @todo go through and declare all of our types here.

/**
 * The product type taxonomy:
 * @see /admin/structure/taxonomy/manage/product_type/overview
 */
export interface ProductTypeTaxonomyTerm extends Omit<DrupalTaxonomyTerm, 'description'> {
  description: {
    processed?: string,
    value?: string,
    format?: string
  }
}

/**
 * The product class taxonomy:
 * @see /admin/structure/taxonomy/manage/product_class/overview
 */
export interface ProductClassTaxonomyTerm extends Omit<DrupalTaxonomyTerm, 'description'> {
  description: {
    processed?: string,
    value?: string,
    format?: string
  }
}

/**
 * The industry segments type taxonomy:
 * @see /admin/structure/taxonomy/manage/product_type/overview
 */
export interface IndustrySegmentsTaxonomyTerm extends Omit<DrupalTaxonomyTerm, 'description'> {
  description: {
    processed?: string,
    value?: string,
    format?: string
  }
}

export interface DrupalVariationNode extends DrupalNode {
  field_var_number: string,
  field_var_detail: string,
  field_var_sku: string,
  field_var_lyokit_modal: boolean,
  field_var_quantity: DrupalTaxonomyTerm,
  field_var_reactions: DrupalTaxonomyTerm,
  field_var_runs: DrupalTaxonomyTerm,
  field_var_tests: DrupalTaxonomyTerm,
  field_var_volume: DrupalTaxonomyTerm,
}
export interface DrupalProductNode extends DrupalNode {
  field_product_number: string[],
  field_product_additionals: DrupalProductNode,
  field_product_video?: DrupalVideoMedia,
  field_product_training_videos?: DrupalVideoMedia[],
  field_product_image?: DrupalMedia[],
  field_product_vars?: DrupalVariationNode[],
}
export interface DrupalWebform extends JsonApiResourceWithPath {
  drupal_internal__nid: number
  drupal_internal__vid: number
  changed: string
  created: string
  title: string
  default_langcode: boolean
  sticky: boolean
}

export interface DrupalWebformElement {
  '#description'?: string,
  '#format'?: string,
  '#required'?: boolean,
  '#text'?: string,
  '#title'?: string,
  '#type'?: string,
}

/**
 * The document media type.
 */
export interface DrupalDocumentMedia extends DrupalMedia {
  field_document_categories?: DrupalTaxonomyTerm[],
  field_document_industry?: DrupalTaxonomyTerm[],
  field_document_language?: DrupalTaxonomyTerm,
  field_document_region?: DrupalTaxonomyTerm,
  field_tags?: DrupalTaxonomyTerm[],
}

/**
 * The video media type.
 */
export interface DrupalVideoMedia extends DrupalMedia {
  field_media_oembed_video?: string,
  thumbnail?: DrupalFile,
  field_video_thumbnail?: DrupalMedia,
  field_video_categories?: DrupalTaxonomyTerm,
}

export interface DrupalLink {
  uri: string,
  title: string,
  url: string,
  options: {[key: string]: string},
}

export interface BreadCrumb {
  id: string,
  name: string,
  url: string,
}

/**
 * The search api response metadata.
 */
export interface DrupalSearchApiResponseMeta {
  count?: number
  facets?: DrupalSearchApiFacet[]
}

/**
 * The Support terms listed on the support page.
 */
export interface SupportTerm extends Omit<DrupalTaxonomyTerm, 'description'> {
  field_support_icon?: DrupalMedia,
  description?: {
    format?: string,
    processed?: string,
    value?: string,
  },
}

/**
 * The Testing Method terms listed on product type/name listing pages.
 */
export interface TestingMethodTerm extends Omit<DrupalTaxonomyTerm, 'description'> {
  field_testing_method_icon?: DrupalMedia,
  description?: {
    format?: string,
    processed?: string,
    value?: string,
  },
}

/**
 * Provides a color option based upon hygiena colors.
 */
export interface HygienaColorOption {
  color?: "primary"|"accent"|"complimentary"|"tertiary"|"black"|"light"|"white"|"bio-red"|"light-gray"|"gray",
}

/**
 * A variation will include a key and a list of product numbers.
 * {"40ml": ["KIT1349"]}
 * {"96": ["KIT1349", "KIT81745]}
 */
export interface ProductVariationPair {
  id: string,
  lyokit?: boolean,
  label: string,
  productNumber: string[],
}
export interface ProductVariations {
  // The product number from the first variation.
  productNumber: string[],
  reactions?: ProductVariationPair[],
  quantity?: ProductVariationPair[],
  tests?: ProductVariationPair[],
  volume?: ProductVariationPair[],
}


export interface DrupalProductVariationProductNumber {
  number: string;
  sku?: string;
  detail?: string;
  ausStoreLink?: string;
  usStoreLink?: string;
}

export enum DrupalProductVariationType {
  Reactions = 'Reactions',
  Quantity = 'Quantity',
  Volume = 'Volume',
  Tests = 'Tests',
  Runs = 'Runs',
}

export interface DrupalProductVariationGroup {
  productNumbers: DrupalProductVariationProductNumber[];
  modal: boolean;
  variation?: {
    type?: DrupalProductVariationType;
    value?: string|number;
  }
}

export interface DrupalTaxonomyVocabulary {
  id: string,
  resourceIdObjMeta: {
    drupal_internal__target_id: string,
  },
  type: string,
}

export interface DrupalMainMenuLinkContent extends DrupalMenuLinkContent {
  field_taxonomy_menu?: DrupalTaxonomyVocabulary,
  field_utility_taxonomy_menu?: DrupalTaxonomyVocabulary,
  submenu?: DrupalTaxonomyTermSubMenu,
  utilityItems?: DrupalTaxonomyTerm[],
  field_menu_item_cta?: boolean,
}

export interface DrupalEmptyNavMenuLinkContent extends DrupalMenuLinkContent {
  field_menu_id: string|number;
  field_menu_icon?: DrupalTaxonomyTerm,
}

export interface DrupalTaxonomyTermSubMenuItem {
  id: string,
  name: string,
  weight: number,
  image?: DrupalMedia,
  displayTitle?: string,
  path: {
    alias: string,
  }
  altLink?: string,
  children?: DrupalTaxonomyTermSubMenuItem[],
}

export interface DrupalTaxonomyTermSubMenu {
  menu: DrupalTaxonomyVocabulary,
  items?: DrupalTaxonomyTermSubMenuItem[],
}

/**
 * Contains an image url to plaiceholder image string.
 */
export interface BlurMap {
  [key: string]: {
    base64: string,
  }
}

export interface ParagraphSectionMap {
  sections: DrupalParagraph[],
  sectionMap: {
    [key: string]: DrupalParagraph
  }
}

export type JsonApiGetUrlOnly = {
  urlOnly?: boolean,
}

export type ProductFinderProduct = {
  id?: string|number,
  title: string,
  hero_image: DrupalMedia,
  brand: {
    id?: string,
    name?: string,
  },
  category: {
    id?: string,
    name?: string,
    image?: DrupalMedia,
  },
  sub_category: {
    id?: string,
    name?: string,
  },
  path?: PathAlias,
}

export interface JsonApiExtrasLink {
  url: string,
  uri: string,
  title: string,
  options: {
    'data-entity-substitution': string,
    'data-entity-type': string,
    'data-entity-uuid': string,
  },
}

export interface CustomMenuLink extends JsonApiExtrasLink {}
export interface MapMarkerCoordinates {
  x: number;
  y: number;
  name?: string,
  active?: boolean,
}

export type DocumentFilter = {
  id: string,
  name: string,
  element_id: string
}

export type Certificate = {
  id: string,
  lotNumber: string,
  expiration: string,
  product?: DrupalProductNode,
  label: string,
  file?: DrupalMedia,
}

export type FacetFilters = {[key: string]: string|string[]|null};

export type DrupalSearchApiTerm = {
  url: string,
  values: {
    value: string,
    label: string,
    active?: boolean,
    count?: number,
  }
}

export type Dictionary = {
  [key: string]: {
    [key: string]: string,
  }
}

export type TranslatedUrls = {
  en: string,
  es: string,
  'pt-br': string,
  de: string,
  fr: string,
}

export enum WorkflowType {
  Additionals = 'additionals',
  Automation = 'automation',
  Equipment = 'equipment',
  Extractions = 'extractions',
  Instruments = 'instruments',
  Media = 'media',
  Monitoring = 'monitoring',
  SampleCollection = 'sample_collection',
  Software = 'software',
}